import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { ExtendedColorPalette } from '@acceligentllc/shared/enums/color';
import { DefaultColor } from '@acceligentllc/shared/enums/color';

import type { RootState } from 'af-reducers';

import * as ColorUtils from 'af-utils/color.util';

interface SkillGridItem {
	color: ExtendedColorPalette;
	show: boolean;
}

interface OwnProps {
	withBackground?: boolean;
	skills?: { color: ExtendedColorPalette; }[];
	showAll?: boolean;
	enableCdlIndicator?: boolean;
	size?: 'small' | 'medium' | 'large';
	forceCdlIndicator?: boolean;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

// company object needs to be loaded in redux form
const SkillsColorGrid: React.FC<Props> = (props) => {
	const {
		enableCdlIndicator = false,
		size = 'small',
		showAll = false,
		skillColorGrid = [],
		withBackground = true,
		forceCdlIndicator = false,
		cdlInSkillsGrid,
		skills,
	} = props;

	const [skillGrid, setSkillGrid] = React.useState<SkillGridItem[][]>([]);

	React.useEffect(() => {
		const skillTypes = {};
		if (skills?.length) {
			for (const _skill of skills) {
				if (_skill?.color) {
					skillTypes[_skill.color.toUpperCase()] = true;
				}
			}
		}

		const _skillGrid: SkillGridItem[][] = [];
		for (let _rowIndex = 0; _rowIndex < skillColorGrid!.length; _rowIndex++) {
			_skillGrid.push([]);
			for (const _color of skillColorGrid![_rowIndex]) {
				_skillGrid[_rowIndex].push({
					color: _color,
					show: !!showAll || skillTypes[_color.toUpperCase()],
				});
			}
		}
		if (forceCdlIndicator || (cdlInSkillsGrid && enableCdlIndicator && _skillGrid[0] && _skillGrid[0][0])) {
			_skillGrid[0][0].color = DefaultColor.CDL_AS_SKILL;
			_skillGrid[0][0].show = true;
		}

		setSkillGrid(_skillGrid);
	}, [cdlInSkillsGrid, enableCdlIndicator, forceCdlIndicator, showAll, skillColorGrid, skills]);

	return (
		<div className={`skills ${skillGrid.length && withBackground ? 'skills--with-background' : ''}`}>
			{skillGrid.map((row: SkillGridItem[], i1: number) => (
				<div className="skills__row" key={i1}>
					{row.map((item: SkillGridItem, i2: number) => (
						<div className={`skills__wrapper skills__wrapper--${size}`} key={i2}>
							<div className={`skills__skill skills__skill--square ${item.show ? ColorUtils.getColorBackgroundClass(item.color) : ''}`} />
						</div>
					))}
				</div>
			))}
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { company } = state.company;

	return {
		skillColorGrid: company?.skillColorGrid ?? undefined,
		cdlInSkillsGrid: company?.cdlInSkillsGrid ?? false,
	};
}

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(SkillsColorGrid);
